<template>
  <div class="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
    <label class="text-sm font-medium text-gray-700 whitespace-nowrap">Filter by date:</label>

    <!-- Visual Bar with Slider -->
    <div class="flex-1 flex items-center gap-3">
      <span class="text-xs text-gray-500">7d</span>

      <div class="flex-1 relative h-6">
        <!-- Background track -->
        <div class="absolute inset-0 bg-gray-200 rounded-md overflow-hidden">
          <!-- Filled portion (grows from left as slider moves right) -->
          <div
            class="absolute left-0 top-0 bottom-0 bg-indigo-500 rounded-md transition-all duration-150 ease-out"
            :style="{ width: fillPercentage + '%' }"
          ></div>
        </div>

        <!-- Range input overlay -->
        <input
          type="range"
          :min="0"
          :max="filterOptions.length - 1"
          v-model.number="sliderValue"
          class="slider-input absolute inset-0 w-full h-full cursor-pointer"
        />
      </div>

      <span class="text-xs text-gray-500">All</span>
    </div>

    <!-- Current selection label -->
    <span class="text-sm font-medium text-indigo-600 min-w-[100px] text-right">
      {{ currentLabel }}
    </span>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const emit = defineEmits(['update:days'])

const props = defineProps({
  initialDays: {
    type: Number,
    default: 0
  }
})

// Options ordered from most restrictive (7 days) to least (all time)
// Slider: left = 7 days (small bar), right = all time (full bar)
const filterOptions = [
  { days: 7, label: 'Last 7 days', fill: 15 },
  { days: 30, label: 'Last 30 days', fill: 30 },
  { days: 90, label: 'Last 90 days', fill: 50 },
  { days: 180, label: 'Last 180 days', fill: 70 },
  { days: 365, label: 'Last 365 days', fill: 85 },
  { days: 0, label: 'All time', fill: 100 }
]

// Find initial slider position based on initialDays
const getInitialSliderValue = () => {
  const index = filterOptions.findIndex(opt => opt.days === props.initialDays)
  // Default to last option (All time) if not found
  return index >= 0 ? index : filterOptions.length - 1
}

const sliderValue = ref(getInitialSliderValue())

const currentLabel = computed(() => {
  return filterOptions[sliderValue.value]?.label || 'All time'
})

const currentDays = computed(() => {
  return filterOptions[sliderValue.value]?.days || 0
})

const fillPercentage = computed(() => {
  return filterOptions[sliderValue.value]?.fill || 100
})

watch(sliderValue, (newValue) => {
  emit('update:days', filterOptions[newValue]?.days || 0)
})
</script>

<style scoped>
/* Custom slider styling */
.slider-input {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  z-index: 10;
}

/* Webkit (Chrome, Safari, Edge) */
.slider-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 24px;
  background: white;
  border: 2px solid #6366f1;
  border-radius: 4px;
  cursor: grab;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.1s ease;
}

.slider-input::-webkit-slider-thumb:hover {
  transform: scale(1.1);
  border-color: #4f46e5;
}

.slider-input::-webkit-slider-thumb:active {
  cursor: grabbing;
  transform: scale(1.05);
}

.slider-input::-webkit-slider-runnable-track {
  height: 100%;
  background: transparent;
}

/* Firefox */
.slider-input::-moz-range-thumb {
  width: 18px;
  height: 24px;
  background: white;
  border: 2px solid #6366f1;
  border-radius: 4px;
  cursor: grab;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.1s ease;
}

.slider-input::-moz-range-thumb:hover {
  transform: scale(1.1);
  border-color: #4f46e5;
}

.slider-input::-moz-range-thumb:active {
  cursor: grabbing;
}

.slider-input::-moz-range-track {
  height: 100%;
  background: transparent;
}

/* Focus styles */
.slider-input:focus {
  outline: none;
}

.slider-input:focus::-webkit-slider-thumb {
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.3);
}

.slider-input:focus::-moz-range-thumb {
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.3);
}
</style>
